import { addSnackbar, doLogout } from 'src/domains/global/actions'
import { Response } from 'src/domains/global/types'
import { store } from 'src/store'
import * as routes from 'src/domains/global/routes'
import wretch from 'wretch'

export const DATE_FORMAT: string = 'Do MMM YY h:mm:ss A'

const baseReq = () =>
    wretch()
        .url(store.getState().global.options.baseUrl!)
        .auth(`Bearer ${store.getState().global.login.token}`)
        .options({ mode: 'cors' })
        .resolve((_) => _.unauthorized(handle401));

function handle401() {
    const location = store.getState().router.location!;
    if (location.pathname !== routes.ROUTELOGIN) {
        store.dispatch(doLogout());
        store.dispatch(
            addSnackbar(
                'Credentials have expired. Please log in again.',
                'credExpired'
            )
        );
        // throw new Error('Unauthorized');
    }
}

const handle403 = () => {
    store.dispatch(
        addSnackbar(
            'Access denied.',
            'accessDenied'
        )
    )
    throw new Error("Forbidden");
}

export async function httpGet<T>(
    request: RequestInfo,
    signal: AbortSignal | null = null,
    accept?: string
): Promise<Response<T>> {
    let token = store.getState().global.login.token
    let options: RequestInit = {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Authorization': `${token?.token_type} ${token?.access_token}`,
            'Accept': accept !== undefined ? accept : 'application/json'
        }
    }

    if (signal !== null) {
        options.signal = signal
    }

    let response = await fetch(request, options)
    if (response.status === 401) {
        store.dispatch(doLogout())
        store.dispatch(
            addSnackbar(
                'Credentials have expired. Please log in again.',
                'credExpired'
            )
        )
        throw new Error("Unauthorized")
    }
    if (response.status === 403) {
        handle403();
    }

    let text = await response.text()
    let body;

    if (accept === undefined) {
        body = (text === '') ? {} : JSON.parse(text)
    } else {
        body = text
    }

    return {
        body: body as T,
        status: response.status
    }
}

export async function httpPost<T>(
    request: RequestInfo,
    body: any,
    signal: AbortSignal | null = null
): Promise<Response<T>> {
    let token = store.getState().global.login.token
    let options: RequestInit = {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Authorization': `${token?.token_type} ${token?.access_token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    }

    if (signal !== null) {
        options.signal = signal
    }

    let response = await fetch(request, options)

    if (response.status === 401) {
        store.dispatch(doLogout())
        store.dispatch(
            addSnackbar(
                'Credentials have expired. Please log in again.',
                'credExpired'
            )
        )
        throw new Error("Unauthorized")
    }
    if (response.status === 403) {
        handle403();
    }

    let text = await response.text()
    let json = (text === '') ? {} : JSON.parse(text)

    return {
        body: json as T,
        status: response.status
    }
}

export async function httpPatch<T>(
    request: RequestInfo,
    body: any,
    signal: AbortSignal | null = null
): Promise<Response<T>> {
    let token = store.getState().global.login.token
    let options: RequestInit = {
        method: 'PATCH',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Authorization': `${token?.token_type} ${token?.access_token}`,
            'Content-Type': 'application/json-patch+json'
        },
        body: JSON.stringify(body)
    }

    if (signal !== null) {
        options.signal = signal
    }

    let response = await fetch(request, options)
    if (response.status === 401) {
        store.dispatch(doLogout())
        store.dispatch(
            addSnackbar(
                'Credentials have expired. Please log in again.',
                'credExpired'
            )
        )
        throw new Error("Unauthorized")
    }

    let text = await response.text()
    let json = (text === '') ? {} : JSON.parse(text)

    // if (response.status === 400) {
    //     throw json;
    // }

    if (response.status === 403) {
        handle403();
    }

    return {
        body: json as T,
        status: response.status
    }
}

export async function httpDelete<T>(request: RequestInfo): Promise<Response<T>> {
    let token = store.getState().global.login.token
    let options: RequestInit = {
        method: 'DELETE',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Authorization': `${token?.token_type} ${token?.access_token}`,
            'Content-Type': 'application/json'
        }
    }

    let response = await fetch(request, options)
    if (response.status === 401) {
        store.dispatch(doLogout())
        store.dispatch(
            addSnackbar(
                'Credentials have expired. Please log in again.',
                'credExpired'
            )
        )
        throw new Error("Unauthorized")
    }

    let text = await response.text()
    let json = (text === '') ? {} : JSON.parse(text)

    if (response.status === 400) {
        throw new Error(json)
    }

    return {
        body: json as T,
        status: response.status
    }
}

export async function httpPut<T>(
    request: RequestInfo,
    body: any,
    signal: AbortSignal | null = null
): Promise<Response<T>> {
    let token = store.getState().global.login.token
    let options: RequestInit = {
        method: 'PUT',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
            'Authorization': `${token?.token_type} ${token?.access_token}`,
            'Content-Type': 'text/uri-list'
        },
        body
    }

    if (signal !== null) {
        options.signal = signal
    }

    let response = await fetch(request, options)
    if (response.status === 401) {
        store.dispatch(doLogout())
        store.dispatch(
            addSnackbar(
                'Credentials have expired. Please log in again.',
                'credExpired'
            )
        )
        throw new Error("Unauthorized")
    }

    let text = await response.text()
    let json = (text === '') ? {} : JSON.parse(text)

    if (response.status === 400) {
        throw new Error(json)
    }

    return {
        body: json as T,
        status: response.status
    }
}

export function capitalize(str: string) {
    return str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : str;
}

export const hasText: (str: string) => boolean = (str) => /\S/.test(str);
